import React, { useState, useEffect, useMemo } from 'react';
import { Settings, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const OptionsPanel = ({ 
  category, 
  onCategoryChange,
  market,
  onMarketChange,
  markets,
  expos,
  festivals,
  exploreTab,
  onExploreTabChange,
  province,
  onProvinceChange,
  tags, 
  onTagsChange, 
  categories,
  sortBy,
  onSortChange,
  viewLayout,
  onViewChange,
  stallsPerPage,
  onStallsPerPageChange
}) => {
  const [availableTags, setAvailableTags] = useState([]);
  const API_URL = window.location.origin;

  useEffect(() => {
    const fetchTags = async () => {
      try {
        const response = await fetch(`${API_URL}/api/categories/tags`);
        if (response.ok) {
          const data = await response.json();
          setAvailableTags(data);
        }
      } catch (error) {
        console.error('Error fetching tags:', error);
      }
    };
    fetchTags();
  }, [API_URL]);

  // Build the "Browse By" items dynamically based on explore tab
  const browseByItems = useMemo(() => {
    switch (exploreTab) {
      case 'markets': return markets || [];
      case 'expos': return expos || [];
      case 'festivals': return festivals || [];
      default: return [...(markets || []), ...(expos || []), ...(festivals || [])];
    }
  }, [exploreTab, markets, expos, festivals]);

  const browseByLabel = exploreTab === 'markets' ? 'Markets' 
    : exploreTab === 'expos' ? 'Expos' 
    : exploreTab === 'festivals' ? 'Festivals' 
    : 'All';

  const selectClass = "w-full border-2 border-gray-200 dark:border-gray-700 dark:bg-[#1a1a1a] dark:text-white focus:border-[#FFD12D] focus:ring-2 focus:ring-[#FFD12D]/20 transition-all duration-300";
  const itemClass = "dark:text-white dark:focus:bg-[#FFD12D]/20";

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-lg font-semibold text-[#252525] dark:text-white mb-4">
        <Settings className="w-5 h-5 text-[#FFD12D]" />
        <span>Options</span>
      </div>
      
      <div className="p-4 bg-gray-50 dark:bg-[#252525] rounded-xl space-y-3">
        {/* Explore Filter */}
        <div>
          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 block">
            Explore
          </label>
          <Select 
            value={exploreTab} 
            onValueChange={(val) => {
              onExploreTabChange(val);
              onMarketChange('All Markets');
            }}
          >
            <SelectTrigger data-testid="options-explore-filter" className={selectClass}>
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent className="dark:bg-[#1a1a1a] dark:border-gray-700">
              <SelectItem value="all" className={itemClass}>All</SelectItem>
              <SelectItem value="markets" className={itemClass}>Markets</SelectItem>
              <SelectItem value="expos" className={itemClass}>Expos</SelectItem>
              <SelectItem value="festivals" className={itemClass}>Festivals</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Browse by Province (new) */}
        <div>
          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 block">
            Browse by Province
          </label>
          <Select
            value={province || 'all'}
            onValueChange={(val) => onProvinceChange?.(val)}
          >
            <SelectTrigger data-testid="options-province-filter" className={selectClass}>
              <SelectValue placeholder="All Provinces" />
            </SelectTrigger>
            <SelectContent className="dark:bg-[#1a1a1a] dark:border-gray-700">
              <SelectItem value="all" className={itemClass}>All Provinces</SelectItem>
              <SelectItem value="Eastern Cape" className={itemClass}>Eastern Cape</SelectItem>
              <SelectItem value="Free State" className={itemClass}>Free State</SelectItem>
              <SelectItem value="Gauteng" className={itemClass}>Gauteng</SelectItem>
              <SelectItem value="KwaZulu-Natal" className={itemClass}>KwaZulu-Natal</SelectItem>
              <SelectItem value="Limpopo" className={itemClass}>Limpopo</SelectItem>
              <SelectItem value="Mpumalanga" className={itemClass}>Mpumalanga</SelectItem>
              <SelectItem value="Northern Cape" className={itemClass}>Northern Cape</SelectItem>
              <SelectItem value="North West" className={itemClass}>North West</SelectItem>
              <SelectItem value="Western Cape" className={itemClass}>Western Cape</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Browse By - dynamic based on Explore */}
        <div>
          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 block">
            Browse By
          </label>
          <Select 
            value={market === 'All Markets' ? 'all-browse' : market} 
            onValueChange={(val) => onMarketChange(val === 'all-browse' ? 'All Markets' : val)}
          >
            <SelectTrigger data-testid="options-browse-filter" className={selectClass}>
              <SelectValue placeholder={browseByLabel === 'All' ? 'All Locations' : `All ${browseByLabel}`} />
            </SelectTrigger>
            <SelectContent className="dark:bg-[#1a1a1a] dark:border-gray-700">
              <SelectItem value="all-browse" className={itemClass}>
                {browseByLabel === 'All' ? 'All Locations' : `All ${browseByLabel}`}
              </SelectItem>
              {browseByItems.map((item) => (
                <SelectItem key={item} value={item} className={itemClass}>
                  {item}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Category */}
        <div>
          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 block">
            Category
          </label>
          <Select value={category} onValueChange={onCategoryChange}>
            <SelectTrigger data-testid="options-category-filter" className={selectClass}>
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent className="dark:bg-[#1a1a1a] dark:border-gray-700">
              <SelectItem value="All Categories" className={itemClass}>All Categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat} value={cat} className={itemClass}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Tags */}
        <div>
          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 block">
            Filter by Tags
          </label>
          <Select value={tags || "all-tags"} onValueChange={(value) => onTagsChange(value === "all-tags" ? "" : value)}>
            <SelectTrigger data-testid="options-tags-filter" className={selectClass}>
              <SelectValue placeholder="All Tags" />
            </SelectTrigger>
            <SelectContent className="dark:bg-[#1a1a1a] dark:border-gray-700">
              <SelectItem value="all-tags" className={itemClass}>All Tags</SelectItem>
              {availableTags.map((tag) => (
                <SelectItem key={tag} value={tag} className={itemClass}>{tag}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Sort By */}
        <div>
          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 block">
            Sort by
          </label>
          <Select value={sortBy} onValueChange={onSortChange}>
            <SelectTrigger data-testid="options-sort-filter" className={selectClass}>
              <SelectValue placeholder="Sort order" />
            </SelectTrigger>
            <SelectContent className="dark:bg-[#1a1a1a] dark:border-gray-700">
              <SelectItem value="random" className={itemClass}>Random</SelectItem>
              <SelectItem value="name-asc" className={itemClass}>Name - Ascending (A-Z)</SelectItem>
              <SelectItem value="name-desc" className={itemClass}>Name - Descending (Z-A)</SelectItem>
              <SelectItem value="stall-asc" className={itemClass}>Stall Number - Ascending</SelectItem>
              <SelectItem value="stall-desc" className={itemClass}>Stall Number - Descending</SelectItem>
              <SelectItem value="category-asc" className={itemClass}>Category - Ascending</SelectItem>
              <SelectItem value="category-desc" className={itemClass}>Category - Descending</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* View Layout */}
        <div>
          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 block">
            View Layout
          </label>
          <Select value={viewLayout} onValueChange={onViewChange}>
            <SelectTrigger data-testid="options-layout-filter" className={selectClass}>
              <SelectValue placeholder="Select layout" />
            </SelectTrigger>
            <SelectContent className="dark:bg-[#1a1a1a] dark:border-gray-700">
              <SelectItem value="grid-1" className={itemClass}>Grid 1 Column</SelectItem>
              <SelectItem value="grid-2" className={itemClass}>Grid 2 Columns</SelectItem>
              <SelectItem value="grid-4" className={itemClass}>Grid 4 Columns</SelectItem>
              <SelectItem value="list" className={itemClass}>List View</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Stalls Per Page */}
        <div>
          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 block">
            Stalls per Page
          </label>
          <Select value={stallsPerPage} onValueChange={onStallsPerPageChange}>
            <SelectTrigger data-testid="options-perpage-filter" className={selectClass}>
              <SelectValue placeholder="Select per page" />
            </SelectTrigger>
            <SelectContent className="dark:bg-[#1a1a1a] dark:border-gray-700">
              <SelectItem value="all" className={itemClass}>View All</SelectItem>
              <SelectItem value="1" className={itemClass}>1 per page</SelectItem>
              <SelectItem value="10" className={itemClass}>10 per page</SelectItem>
              <SelectItem value="20" className={itemClass}>20 per page</SelectItem>
              <SelectItem value="50" className={itemClass}>50 per page</SelectItem>
              <SelectItem value="100" className={itemClass}>100 per page</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Reset All Button */}
      <Button
        data-testid="options-reset-btn"
        onClick={() => {
          onExploreTabChange('all');
          onMarketChange('All Markets');
          onProvinceChange?.('all');
          onCategoryChange('All Categories');
          onTagsChange('');
          onSortChange('name-asc');
          onViewChange('grid-2');
          onStallsPerPageChange('20');
        }}
        className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold h-11 transition-all duration-300 shadow-md flex items-center justify-center gap-2"
      >
        <RotateCcw className="w-4 h-4" />
        Reset All Options
      </Button>
    </div>
  );
};

export default OptionsPanel;
